import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CommonModal from '../../atoms/modal/CommonModal'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import InputCustom from '../../atoms/input/InputCustom'
import TextArea from '../../atoms/textarea/TextArea'
import FileAttach from '../../atoms/file-attach/FileAttach'
import { formatCommonAmount } from '../../../util/formatAmount'
import { TAX_PERCENTAGE } from '../../../util/constants'
import { toastHelper } from '../../../util/toastHelper'
import {
	getAllProposals,
	updateReviewProposal,
} from '../../../features/proposalSlice'
import PackageSelection from '../../../pages/vendors-list/Modals/PackageSelection'
import AddAddOnModal from './AddAddOnModal'
import {
	normalizeToTree,
	totalFromTree,
	treeToPayload,
	makePackageNode,
	makeAddOnNode,
} from './reviewLineItemTree'

import styles from './Review.module.scss'

function Review({ clickHandler, data, isEdit }) {
	const dispatch = useDispatch()
	const [packages, setPackages] = useState([])
	const [isDisabled, setIsDisabled] = useState(false)
	const [addOnParent, setAddOnParent] = useState(null)
	const [selectedPackage, setSelectedPackage] = useState(null)
	const [showPackageModal, setShowPackageModal] = useState(false)
	const taxPercentage = data?.taxPercentage ?? TAX_PERCENTAGE ?? 10
	const [formData, setFormData] = useState({ note: '', attachment: null })
	const reduxPackages = useSelector((state) => state.packages?.packages) || []

	useEffect(() => {
		setPackages(normalizeToTree(data))
		setFormData({ ...formData, note: data.note ?? '' })
	}, [data?.proposalId, data?.packages, data?.lineItems])

	const { total, tax, grandTotal } = useMemo(() => {
		const subTotal = totalFromTree(packages)
		const taxAmount = subTotal * (taxPercentage / 100)
		return {
			total: subTotal,
			tax: taxAmount,
			grandTotal: subTotal + taxAmount,
		}
	}, [packages])

	const updatePackage = (packageRowId, updater) => {
		setPackages((prev) =>
			prev.map((pkg) => (pkg.rowId === packageRowId ? updater(pkg) : pkg))
		)
	}

	const updateAddOn = (packageRowId, addonRowId, updater) => {
		setPackages((prev) =>
			prev.map((pkg) => {
				if (pkg.rowId !== packageRowId) return pkg
				return {
					...pkg,
					addOns: (pkg.addOns || []).map((a) =>
						a.rowId === addonRowId ? updater(a) : a
					),
				}
			})
		)
	}

	const quantityChange = (val, addon, rowId) => {
		let num = val
		// const pkg = reduxPackages.find((pkg) => pkg._id === rowId)
		// const addOn = pkg?.addOnsIds?.find((a) => a._id === addon?.id)
		if (!!addon?.minQuantity && val < addon?.minQuantity) {
			num = addon?.minQuantity
			toastHelper(
				`Minimum quantity for ${addon?.name} is ${addon?.minQuantity}`,
				'error'
			)
		} else if (!!addon?.maxQuantity && val > addon?.maxQuantity) {
			num = addon?.maxQuantity
			toastHelper(
				`Maximum quantity for ${addon?.name} is ${addon?.maxQuantity}`,
				'error'
			)
		}
		handleChange(addon.rowId, 'quantity', num, {
			isAddOn: true,
			packageRowId: rowId,
		})
	}

	const handleChange = (rowId, key, value, options = {}) => {
		const { isAddOn, packageRowId } = options
		const isQuantityOrPrice = key === 'quantity' || key === 'unitPrice'
		if (isQuantityOrPrice && (value <= 0 || value > 99999)) {
			if (value > 99999)
				toastHelper('Enter a value less than 100000', 'error')
			return
		} else if (isAddOn && packageRowId) {
			updateAddOn(packageRowId, rowId, (addon) => {
				if (isQuantityOrPrice) {
					const num = parseFloat(value)
					return { ...addon, [key]: isNaN(num) ? 0 : num }
				}
				return { ...addon, [key]: value }
			})
		} else {
			setPackages((prev) =>
				prev.map((pkg) => {
					if (pkg.rowId !== rowId) return pkg
					if (isQuantityOrPrice) {
						const num = parseFloat(value)
						return { ...pkg, [key]: isNaN(num) ? 0 : num }
					}
					return { ...pkg, [key]: value }
				})
			)
		}
	}

	const handleDeletePackage = (packageRowId) => {
		setPackages((prev) => prev.filter((p) => p.rowId !== packageRowId))
	}

	const handleDeleteAddOn = (packageRowId, addonRowId) => {
		updatePackage(packageRowId, (pkg) => ({
			...pkg,
			addOns: (pkg.addOns || []).filter((a) => a.rowId !== addonRowId),
		}))
	}

	const addCustomLineItem = () => {
		const tempId = `_custom_${Date.now()}_${Math.random().toString(36).slice(2)}`
		setPackages((prev) => [
			...prev,
			makePackageNode({
				id: tempId,
				name: '',
				quantity: 1,
				unitPrice: 0,
				type: 'custom',
				addOns: [],
				image: '',
			}),
		])
		setShowPackageModal(false)
	}

	const addFromPackage = () => {
		if (!selectedPackage) {
			toastHelper('Please select a package', 'error')
			return
		}
		const pkgId = selectedPackage._id
		const newAddOns = (selectedPackage.selectedAddOns || [])
			.map((addonId) => {
				const addon = (selectedPackage.addOnsIds || []).find(
					(a) => a?._id === addonId
				)
				return addon
					? {
							id: addon._id,
							name: addon.name,
							quantity: addon.minQuantity || 1,
							unitPrice: parseFloat(addon.price) || 0,
							type: 'addOn',
							image: addon.image || '',
							minQuantity: addon.minQuantity || 0,
							maxQuantity: addon.maxQuantity || 0,
						}
					: null
			})
			.filter(Boolean)

		setPackages((prev) => {
			const existingIdx = prev.findIndex((p) => p.id === pkgId)
			if (existingIdx >= 0) {
				const existing = prev[existingIdx]
				const next = [...prev]
				next[existingIdx] = {
					...existing,
					quantity: (Number(existing.quantity) || 0) + 1,
					addOns: (() => {
						const addOns = [...(existing.addOns || [])]
						newAddOns.forEach((newA) => {
							const idx = addOns.findIndex(
								(a) => a.id === newA.id
							)
							if (idx >= 0) {
								addOns[idx] = {
									...addOns[idx],
									quantity:
										(Number(addOns[idx].quantity) || 0) + 1,
								}
							} else {
								addOns.push(
									makeAddOnNode(newA, {
										parentPackageId: existing.rowId,
										index: addOns.length,
									})
								)
							}
						})
						return addOns
					})(),
				}
				return next
			}
			const newPkg = makePackageNode({
				id: selectedPackage._id,
				name: selectedPackage.name,
				quantity: 1,
				unitPrice: parseFloat(selectedPackage.price) || 0,
				type: 'package',
				addOns: [],
				image: selectedPackage.image || '',
			})
			newPkg.rowId = newPkg.rowId ?? `pkg_${pkgId}`
			newPkg.addOns = newAddOns.map((a, i) =>
				makeAddOnNode(a, { parentPackageId: newPkg.rowId, index: i })
			)
			return [...prev, newPkg]
		})
		setShowPackageModal(false)
		setSelectedPackage(null)
	}

	const addLineItem = () => {
		if (reduxPackages?.length > 0) setShowPackageModal(true)
		else addCustomLineItem()
	}

	const openAddOnModal = (pkg) => {
		setAddOnParent(pkg)
	}

	const addAddOnToParent = (addonNode) => {
		if (!addOnParent) return
		setPackages((prev) =>
			prev.map((pkg) => {
				if (pkg.rowId !== addOnParent.rowId) return pkg
				const addOns = [...(pkg.addOns || [])]
				const existingIdx = addOns.findIndex(
					(a) => a.id === addonNode.id
				)
				if (existingIdx >= 0) {
					const qty = Number(addOns[existingIdx].quantity) || 0
					const max = addOns[existingIdx].maxQuantity
					addOns[existingIdx] = {
						...addOns[existingIdx],
						quantity: !!max && qty + 1 > max ? max : qty + 1,
					}
				} else {
					addOns.push({
						...addonNode,
						quantity: addonNode.minQuantity || 1,
						rowId:
							addonNode.rowId ||
							`addon_${pkg.rowId}_${addonNode.id}_${Date.now()}`,
					})
				}
				return { ...pkg, addOns }
			})
		)
		setAddOnParent(null)
	}

	const addCustomAddOn = () => {
		if (!addOnParent) return
		const tempId = `_custom_addon_${Date.now()}_${Math.random().toString(36).slice(2)}`
		const customNode = makeAddOnNode(
			{ id: tempId, name: '', quantity: 1, unitPrice: 0, type: 'custom' },
			{ parentPackageId: addOnParent.rowId, rowId: tempId }
		)
		addAddOnToParent(customNode)
		setAddOnParent(null)
	}

	const getSubmitFormData = () => {
		const fd = new FormData()
		fd.append('lineItems', JSON.stringify(treeToPayload(packages)))
		fd.append('subtotal', total || 0)
		fd.append('tax', tax || 0)
		fd.append('total', grandTotal || data?.userBudget || 0)
		fd.append('bidAmount', grandTotal || data?.userBudget || 0)
		fd.append('note', formData.note ?? '')
		if (formData.attachment) fd.append('image', formData.attachment)
		return fd
	}

	const handleSubmit = async () => {
		try {
			// if (!packages.length) {toastHelper('Add at least one line item', 'error')return}
			if (!!packages.length) {
				const emptyItems = packages?.filter(
					(pkg) => !pkg.name || !pkg.unitPrice
				)
				const emptyAddOns = packages
					?.flatMap((pkg) => pkg.addOns)
					?.filter((pkg) => !pkg.name || !pkg.unitPrice)
				if (!!emptyItems?.length || !!emptyAddOns?.length) {
					toastHelper('Please fill all the line items', 'error')
					return
				}
			}
			const proposalId = data?.proposalId
			if (!proposalId) {
				toastHelper('Proposal not found', 'error')
				return
			}
			let msg = ''
			packages?.forEach((pkg) => {
				if (!pkg?.quantity)
					msg += `Package "${pkg.name}" has no quantity. `
				if (!pkg?.unitPrice)
					msg += `Package "${pkg.name}" has no unit price. `
				pkg.addOns?.forEach((addon) => {
					if (!addon?.quantity)
						msg += `Addon "${addon.name}" has no quantity. `
					if (!addon?.unitPrice)
						msg += `Addon "${addon.name}" has no unit price. `
				})
			})
			if (msg) {
				toastHelper(msg, 'error')
				return
			}
			if (!grandTotal && !data?.userBudget) {
				toastHelper('Please add at least one line item', 'error')
				return
			}
			setIsDisabled(true)
			const payload = getSubmitFormData()
			await dispatch(
				updateReviewProposal({ proposalId, formData: payload })
			)
				.unwrap()
				.then((res) => {
					if (res?.success) {
						clickHandler()
						dispatch(getAllProposals({ proposalId }))
					}
				})
		} catch (error) {
			console.log(error)
		} finally {
			setTimeout(() => setIsDisabled(false), 4000)
		}
	}

	return (
		<>
			<CommonModal
				open={true}
				handleClose={clickHandler}
				heading={!isEdit ? 'Review' : 'Edit Proposal'}
				modalInner={styles.ReviewModalInner}
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							type="button"
							size="lg"
							onClick={clickHandler}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							type="button"
							size="lg"
							disabled={isDisabled}
							onClick={handleSubmit}
						>
							{isDisabled ? 'Submitting...' : 'Submit Bid'}
						</ButtonCustom>
					</>
				}
			>
				<div className={styles.formWrapper}>
					<div className={styles.heading}>
						<h1>Line Items</h1>
						<ButtonCustom
							variant="default"
							size="md"
							onClick={addLineItem}
						>
							+ Add Item
						</ButtonCustom>
					</div>

					{packages?.filter((p) => !!p.id || !!p._id)?.length > 0 && (
						<>
							<div className={styles.lineItems}>
								<div className={styles.header}>
									<div className={styles.headerCell}>
										Item Name
									</div>
									<div className={styles.headerCell}>
										Quantity
									</div>
									<div className={styles.headerCell}>
										Unit Price
									</div>
									<div className={styles.headerCell}>
										Amount
									</div>
									<div className={styles.headerCell}></div>
								</div>

								<div className={styles.body}>
									{packages.map((pkg) => (
										<React.Fragment key={pkg.rowId}>
											<div className={styles.bodyCell}>
												<InputCustom
													type="text"
													value={pkg.name}
													onChange={(e) =>
														handleChange(
															pkg.rowId,
															'name',
															e.target.value
														)
													}
												/>
												<InputCustom
													type="number"
													min={0}
													value={pkg.quantity}
													onChange={(e) =>
														handleChange(
															pkg.rowId,
															'quantity',
															e.target.value
														)
													}
												/>
												<InputCustom
													type="number"
													min={0}
													step="0.01"
													value={pkg.unitPrice}
													onChange={(e) =>
														handleChange(
															pkg.rowId,
															'unitPrice',
															e.target.value
														)
													}
												/>
												<div
													className={
														styles.bodyCellContentItem
													}
												>
													{formatCommonAmount(
														(Number(pkg.quantity) ||
															0) *
															(parseFloat(
																pkg.unitPrice
															) || 0)
													)}
												</div>
												<div
													className={
														styles.bodyCellContentItem
													}
												>
													<ButtonCustom
														variant="outlined"
														size="md"
														onClick={() =>
															handleDeletePackage(
																pkg.rowId
															)
														}
													>
														<img
															src="/images/trash.svg"
															alt="delete"
														/>
													</ButtonCustom>
												</div>
											</div>
											{(pkg.addOns || []).map((addon) => (
												<div
													className={styles.bodyCell}
													key={addon.rowId}
												>
													<div
														className={
															styles.bodyCellContentItemAddon
														}
													>
														<img
															src="/images/curve-line-icon.svg"
															alt="curve line"
															width={24}
															height={24}
														/>
														<InputCustom
															type="text"
															value={addon.name}
															onChange={(e) =>
																handleChange(
																	addon.rowId,
																	'name',
																	e.target
																		.value,
																	{
																		isAddOn: true,
																		packageRowId:
																			pkg.rowId,
																	}
																)
															}
														/>
													</div>
													<InputCustom
														type="number"
														min={0}
														value={addon.quantity}
														onChange={(e) =>
															quantityChange(
																e.target.value,
																addon,
																pkg.rowId
															)
														}
													/>
													<InputCustom
														type="number"
														min={0}
														step="0.01"
														value={addon.unitPrice}
														onChange={(e) =>
															handleChange(
																addon.rowId,
																'unitPrice',
																e.target.value,
																{
																	isAddOn: true,
																	packageRowId:
																		pkg.rowId,
																}
															)
														}
													/>
													<div
														className={
															styles.bodyCellContentItem
														}
													>
														{formatCommonAmount(
															(Number(
																addon.quantity
															) || 0) *
																(parseFloat(
																	addon.unitPrice
																) || 0)
														)}
													</div>
													<div
														className={
															styles.bodyCellContentItem
														}
													>
														<ButtonCustom
															variant="outlined"
															size="md"
															onClick={() =>
																handleDeleteAddOn(
																	pkg.rowId,
																	addon.rowId
																)
															}
														>
															<img
																src="/images/trash.svg"
																alt="delete"
															/>
														</ButtonCustom>
													</div>
												</div>
											))}
											<div className={styles.addAddOnRow}>
												<ButtonCustom
													variant="outlined"
													size="md"
													onClick={() =>
														openAddOnModal(pkg)
													}
													className={
														styles.addAddOnButton
													}
												>
													<img
														src="/images/plus-icon.svg"
														alt="plus"
													/>{' '}
													Add Add-on
												</ButtonCustom>
											</div>
										</React.Fragment>
									))}
								</div>
							</div>

							<hr />
							<div className={styles.summaryWrapper}>
								<div className={styles.summary}>
									<div className={styles.summaryItem}>
										Sub Total:
									</div>
									<div className={styles.summaryValue}>
										{formatCommonAmount(total)}
									</div>
									<div className={styles.summaryItem}>
										Tax({taxPercentage}%):
									</div>
									<div className={styles.summaryValue}>
										{formatCommonAmount(tax)}
									</div>
									<div
										className={`${styles.summaryItem} ${styles.total}`}
									>
										Total:
									</div>
									<div
										className={`${styles.summaryValue} ${styles.total}`}
									>
										{formatCommonAmount(grandTotal)}
									</div>
								</div>
							</div>
						</>
					)}

					{/* <div className={styles.formField}><label htmlFor="bidAmount" className={styles.label}>Bid Amount</label>
						<InputCustom type="number" min={0} value={updatedBidAmount} onChange={(e) =>setUpdatedBidAmount(e.target.value)}/></div> */}

					<div className={styles.formField}>
						<label htmlFor="note" className={styles.label}>
							Notes
						</label>
						<TextArea
							label="Notes"
							placeholder="Add Notes or customizations"
							value={formData.note}
							onChange={(e) => {
								setFormData((prev) => ({
									...prev,
									note: e.target.value,
								}))
							}}
						/>
					</div>

					<div className={styles.formField}>
						<label htmlFor="Attachment" className={styles.label}>
							Attachment
						</label>
						<FileAttach
							label="Choose File"
							placeholder={
								data.attachment
									? 'Package Details.pdf'
									: 'No Files Selected'
							}
							accept=".pdf"
							onChange={(e) => {
								setFormData((prev) => ({
									...prev,
									attachment: e?.target?.files?.[0] ?? null,
								}))
							}}
						/>
					</div>
				</div>
			</CommonModal>

			{showPackageModal && (
				<CommonModal
					open={true}
					handleClose={() => {
						setShowPackageModal(false)
						setSelectedPackage(null)
					}}
					heading="Add from package"
					footerContent={
						<>
							<ButtonCustom
								variant="outlined"
								size="lg"
								onClick={addCustomLineItem}
							>
								Add custom line
							</ButtonCustom>
							<ButtonCustom
								variant="default"
								size="lg"
								onClick={addFromPackage}
							>
								Add to line items
							</ButtonCustom>
						</>
					}
				>
					<PackageSelection
						selectedPackage={selectedPackage}
						setSelectedPackage={setSelectedPackage}
					/>
				</CommonModal>
			)}

			{addOnParent && (
				<AddAddOnModal
					open={!!addOnParent}
					onClose={() => setAddOnParent(null)}
					parentPackage={addOnParent}
					onSelect={addAddOnToParent}
					onCustom={addCustomAddOn}
				/>
			)}
		</>
	)
}

export default Review
